// imports the React Javascript Library
import { useEffect, useState } from "react";

import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";

import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { IconButton, Tooltip } from "@mui/material";
import Grow from "@mui/material/Grow";
import { getIn, useFormikContext } from "formik";
import styled from "styled-components";
import { theme } from "../Customization/Theme";
import { ChatbotConsoleService } from "../Services/Apis/ChatbotConsoleService";
import { LoaderComponentProps } from "./Loader";
import { HocBackdropLoader } from "../HOC/HocBackdropLoader";
import { useNotification } from "../Hooks/useNotification/useNotification";
import { bytesToMBDecimal } from "../Utils";

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
];

enum ImageState {
  INITIAL = "initial",
  SEARCH = "search",
  GALLERY = "gallery",
  UPLOADED = "uploaded",
}

interface Props extends LoaderComponentProps {
  name: string;
  sizeLimit: number;
  sizeErrorMsg: string;
  formatErrorMsg: string;
  squareImageBox?: boolean;
  urlImage?: boolean;
  isResolveVariable ? :boolean
}
interface PropsState {
  mainState: any; // initial, search, gallery, uploaded
  imageUploaded: any;
  selectedFile: any;
  fileReader?: any;
  filename?: any;
}

const ImageUploadCard = (props: Props) => {
  const {
    setFieldTouched,
    handleChange,
    errors,
    touched,
    values,
    setFieldValue,
    setFieldError,
    
  } = useFormikContext<any>();

  const [mainState, setMainState] = useState<ImageState>(ImageState.INITIAL); // initial, search, gallery, uploaded
  const { showAlert } = useNotification();
  

  useEffect(() => {
    if (
      (getIn(values, props?.name)?.source &&
      getIn(values, props?.name)?.source?.length > 0) || (props?.urlImage && getIn(values, props?.name)?.length >  10) 
    ) {
      setMainState(ImageState.UPLOADED);
    } else {
      setMainState(ImageState.INITIAL);

    }
  }, [getIn(values, props?.name)]); //eslint-disable-line

  const handleUploadClick = (event: any) => {
    setFieldTouched(props.name, true);

    const inputElement = event?.target as HTMLInputElement | undefined;
    const file = inputElement?.files?.[0];
    if (!file) {
      return;
    }

    // Clear previous error so the same validation message can be shown again.
    setFieldError(props.name, undefined);

    const reader = new FileReader();
    reader?.readAsDataURL(file);

    if (props.sizeLimit && file.size > props.sizeLimit) {
      setTimeout(() => {
        setFieldError(props.name, props.sizeErrorMsg);
        showAlert(`Image size must be smaller than ${bytesToMBDecimal(props.sizeLimit)}MB.`, "error");
      }, 1);
      if (inputElement) {
        inputElement.value = "";
      }
      return;
    }

    reader.onloadend = (e: any) => {
      var mimeType = e?.target?.result
        ?.split(",")[0]
        .split(":")[1]
        .split(";")[0];

      if (!ALLOWED_IMAGE_TYPES.includes(mimeType)) {
        setTimeout(() => {
          setFieldError(props.name, props.formatErrorMsg);
        }, 1);
        if (inputElement) {
          inputElement.value = "";
        }
        return;
      }
      onImageChange(file);
      if (inputElement) {
        inputElement.value = "";
      }
    };
  };
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUploadClick({ target: { files: e.dataTransfer.files } });
    }
  };
  
  const onImageChange = (file: any) => {
    setFieldTouched(props.name, true);
    if(props?.isResolveVariable && file){
      props.setLoading(true);
  
      const formData = new FormData();
      formData.append("source", file);

      ChatbotConsoleService.uploadWhatsappMedia(formData,!props?.isResolveVariable)
        .then((response: any) => {
          setFieldValue(props?.name, response?.data);
          setMainState(ImageState.UPLOADED);
        })
        .catch((error: any) => {
          setMainState(ImageState.INITIAL);
          setFieldValue(props?.name, null);
        })
        .finally(() => {
          props.setLoading(false);
        });
    }else{
      if (file) {
        props.setLoading(true);
  
        const formData = new FormData();
        formData.append("source", file);
  
        ChatbotConsoleService.uploadFile(formData)
          .then((response: any) => {
            setFieldValue(props?.name, response?.data);
            setMainState(ImageState.UPLOADED);
          })
          .catch((error: any) => {
            setMainState(ImageState.INITIAL);
            setFieldValue(props?.name, null);
          })
          .finally(() => {
            props.setLoading(false);
          });
      }
    }

  };

  const imageResetHandler = () => {
    setMainState(ImageState.INITIAL);
    setFieldValue(props?.name, null);
  };

  const renderInitialState = () => {
    return (
      <StyledIntialState>
        <input
          accept="image/*"
          id={props.name}
          multiple
          type="file"
          onChange={handleUploadClick}
        // ref={inputRef}
        />
        <label htmlFor={props.name} className="customInput">
          <Tooltip
            arrow
            enterDelay={1}
            leaveDelay={0}
            TransitionComponent={Grow}
            placement="top"
            title={"Upload"}
          >
            {props.squareImageBox ? (
              <SquareImageBox>
                <AddPhotoAlternateIcon />
              </SquareImageBox>
            ) : (
              <IconButton component="span">
                <AddPhotoAlternateIcon />
              </IconButton>
            )}
          </Tooltip>
        </label>
      </StyledIntialState>
    );
  };
  
  const renderUploadedState = () => {
    return (
      <StyledUploadedState>
        <StyledUploadedImageWrap>
          <img
            loading="lazy"
            src={getIn(values, props?.name)?.source ? getIn(values, props?.name)?.source :  props?.urlImage ? getIn(values, props?.name) :  null}
            style={{ borderRadius: props.squareImageBox ? "4px" : "50%" }}
            alt="media"
          />
          <IconButton onClick={() => imageResetHandler()}>
            <ClearRoundedIcon />
          </IconButton>
        </StyledUploadedImageWrap>
      </StyledUploadedState>
    );
  };

  // useEffect(() => {
  //   props.setLoading(false);
  // }, []);

  return (
    <StyledViewWrap
    onDragEnter={handleDrag}
    onDragLeave={handleDrag}
    onDragOver={handleDrag}
    onDrop={handleDrop}
    className={dragActive ? "drag-active" : ""}>
      {(mainState == ImageState.INITIAL && renderInitialState()) ||
        (mainState == ImageState.UPLOADED && renderUploadedState())}
    </StyledViewWrap>
  );
};

export default HocBackdropLoader(ImageUploadCard);

const StyledUploadedImageWrap = styled.div`
  && {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    svg {
      cursor: pointer;
      font-size: 1.1rem;
      color: ${theme.palette.default.darkGrey};
    }
  }
`;

const StyledViewWrap = styled.div`
  && {
    height: 70px;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.5rem;
    padding: 0 0.5rem;

    input {
      display: none;
    }
  }
`;

const StyledIntialState = styled.div`
  && {
    width: 100%;
    height: 100%;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 1rem;

    .MuiFab-root {
      width: 48px !important;
      height: 48px !important;
      color: ${({ theme }: any) => theme.palette.primary.main};
    }

    .MuiIconButton-root {
      width: 48px !important;
      height: 48px !important;
      color: ${({ theme }: any) => theme.palette.primary.main};
      background-color: ${theme.palette.secondary.main};
      border-radius: 50%;
    }
  }
`;

const StyledUploadedState = styled.div`
  && {
    width: 100%;
    height: 100%;
    overflow: hidden;

    display: flex;
    align-items: center;

    img {
      width: 52px !important;
      height: 52px !important;
      object-fit: cover !important;
      overflow: hidden;
    }
  }
`;

const SquareImageBox = styled.div`
  && {
    width: 64px;
    height: 64px;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background-color: ${theme.palette.default.lightGrey};
    border-radius: 6px;
  }
`;
